import { getSentryConfig } from './config';
import { flushSentry, logSentryConfig } from './helpers';
import { trackSentryException } from '../../../lib/analytics';

let sentryInitialized = false;

export async function initSentry(): Promise<boolean> {
  const config = getSentryConfig();
  logSentryConfig(config);

  if (sentryInitialized) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] already initialized');
    return true;
  }

  if (!config.dsn) {
    sentryInitialized = false;
    // eslint-disable-next-line no-console
    console.warn('[sentry] disabled: missing VITE_SENTRY_DSN');
    return false;
  }

  try {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      tracesSampleRate: config.tracesSampleRate,
      debug: config.debug,
      initialScope: {
        tags: {
          service: config.serviceName,
          runtime: 'browser',
          serverName: config.serverName,
        },
      },
      // GA4 bridge: forwards Sentry exceptions to gtag so error spikes line up
      // with funnels in GA4. NOTE: the admin portal does not load gtag.js today,
      // so this branch is inert until GA4 is added to admin (index.html + a
      // VITE_GA_MEASUREMENT_ID). Kept here intentionally to mirror the user
      // frontend and avoid a second migration later. See
      // docs/discussion/analytics_monitoring_decision.md.
      beforeSend(event) {
        if (event.exception && event.event_id) {
          trackSentryException(
            event.exception.values?.[0]?.value || 'Unhandled exception',
            event.event_id,
          );
        }
        return event;
      },
    });

    Sentry.setTag('service', config.serviceName);
    Sentry.setTag('runtime', 'browser');
    Sentry.setTag('serverName', config.serverName);

    sentryInitialized = Boolean(Sentry.getClient());
    // eslint-disable-next-line no-console
    console.info('[sentry] initialized', {
      initialized: sentryInitialized,
      environment: config.environment,
      release: config.release,
    });

    if (config.smokeTest) {
      const eventId = Sentry.captureException(
        new Error('InviteCloud admin portal Sentry smoke test'),
      );
      // eslint-disable-next-line no-console
      console.info('[sentry] smoke test event captured', { eventId });
      await flushSentry(Sentry, 5000);
    }

    return sentryInitialized;
  } catch (err) {
    sentryInitialized = false;
    // eslint-disable-next-line no-console
    console.warn('[sentry] failed to initialize:', (err as Error).message);
    return false;
  }
}

export async function captureFrontendException(err: unknown): Promise<string | undefined> {
  if (!sentryInitialized) return undefined;

  try {
    const Sentry = await import('@sentry/react');
    const eventId = Sentry.captureException(err);
    // eslint-disable-next-line no-console
    console.info('[sentry] event captured', { eventId });
    await flushSentry(Sentry, 5000);
    return eventId;
  } catch (captureErr) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] capture failed:', (captureErr as Error).message);
    return undefined;
  }
}
